import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Database, ListChecks, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/utils';
import type {
  ToolActionConfirmationEdit,
  ToolActionResponse,
} from '@shared/types/tool-actions';

const STATE_STYLES = {
  create: 'text-emerald-700',
  update: 'text-blue-700',
  unchanged: 'text-domino-inkSoft',
  ambiguous: 'text-amber-700',
  blocked: 'text-amber-700',
} as const;

const EMPTY_CANDIDATES: NonNullable<ToolActionResponse['preview']>['candidates'] = [];

export interface ToolActionConfirmationContext {
  title: string;
  summary: string;
  selectedLabels: string[];
  editableLabels: string[];
  editablePreview: string | null;
  applyChatEdit: (message: string) => boolean;
}

const stripWrappingQuotes = (value: string): string => {
  const trimmed = value.trim();
  const match = trimmed.match(/^["'«“](.*)["'»”]$/s);
  return (match?.[1] ?? trimmed).trim();
};

const extractMemoEdit = (
  message: string,
  hasSingleEditableField: boolean
): { mode: 'replace' | 'append'; text: string } | null => {
  const trimmed = message.trim();
  if (!trimmed || trimmed.endsWith('?')) return null;

  const mentionsMemo = /\b(m[eé]mo|note|hubspot|synth[eè]se|commentaire)\b/i.test(trimmed);
  const replaceMatch =
    trimmed.match(/(?:remplace|modifie|change|mets|mettre|r[eé]dige|ecris|écris|utilise|note|m[eé]mo)[\s\S]{0,120}?(?:par|:)\s*([\s\S]+)$/i)
    ?? trimmed.match(/(?:dis|dire|indique|indiquer|noter)\s+que\s+([\s\S]+)$/i);
  if (replaceMatch && (mentionsMemo || hasSingleEditableField)) {
    const text = stripWrappingQuotes(replaceMatch[1] ?? '');
    return text ? { mode: 'replace', text } : null;
  }

  const appendMatch = trimmed.match(/(?:ajoute|rajoute|compl[eè]te)[\s\S]{0,80}?(?:avec|:)?\s*([\s\S]+)$/i);
  if (appendMatch && mentionsMemo) {
    const text = stripWrappingQuotes(appendMatch[1] ?? '');
    return text ? { mode: 'append', text } : null;
  }

  return null;
};

export const ToolActionConfirmationDialog = ({
  response,
  executing,
  onClose,
  onConfirm,
  onContextChange,
}: {
  response: ToolActionResponse | null;
  executing: boolean;
  onClose: () => void;
  onConfirm: (candidateIds: string[], edits: ToolActionConfirmationEdit[]) => void;
  onContextChange?: (context: ToolActionConfirmationContext | null) => void;
}) => {
  const candidates = response?.preview?.candidates ?? EMPTY_CANDIDATES;
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [editableContentById, setEditableContentById] = useState<Record<string, string>>({});

  useEffect(() => {
    setSelectedIds(
      candidates.filter((candidate) => candidate.selectedByDefault).map((candidate) => candidate.id)
    );
    setEditableContentById(Object.fromEntries(
      candidates
        .filter((candidate) => candidate.editableFields?.includes('contentPreview'))
        .map((candidate) => [candidate.id, candidate.contentPreview ?? ''])
    ));
  }, [response?.confirmationId, candidates]);

  const selectedCount = selectedIds.length;
  const editableCandidates = useMemo(
    () => candidates.filter((candidate) => candidate.editableFields?.includes('contentPreview')),
    [candidates]
  );
  const isCrmPreview = candidates.some((candidate) => (
    candidate.entityType === 'company'
    || candidate.entityType === 'contact'
    || candidate.entityType === 'note'
  ));
  const selectableIds = useMemo(
    () => new Set(
      candidates
        .filter((candidate) => (
          (candidate.state === 'create' || candidate.state === 'update')
          && !candidate.required
        ))
        .map((candidate) => candidate.id)
    ),
    [candidates]
  );
  const confirmationEdits = useMemo<ToolActionConfirmationEdit[]>(
    () => editableCandidates
      .filter((candidate) => selectedIds.includes(candidate.id))
      .map((candidate) => ({
        candidateId: candidate.id,
        contentPreview: editableContentById[candidate.id] ?? candidate.contentPreview ?? '',
      })),
    [editableCandidates, editableContentById, selectedIds]
  );
  const hasInvalidSelectedEdit = confirmationEdits.some((edit) => (
    edit.contentPreview !== undefined
    && edit.contentPreview !== null
    && edit.contentPreview.trim().length === 0
  ));

  const applyChatEdit = useCallback((message: string): boolean => {
    const target = editableCandidates.find((candidate) => selectedIds.includes(candidate.id))
      ?? editableCandidates[0]
      ?? null;
    if (!target) return false;

    const edit = extractMemoEdit(message, editableCandidates.length === 1);
    if (!edit) return false;

    setSelectedIds((current) => (
      current.includes(target.id) ? current : [...current, target.id]
    ));
    setEditableContentById((current) => {
      const currentValue = current[target.id] ?? target.contentPreview ?? '';
      return {
        ...current,
        [target.id]: edit.mode === 'append'
          ? `${currentValue.trim()}\n${edit.text}`.trim()
          : edit.text,
      };
    });
    return true;
  }, [editableCandidates, selectedIds]);

  useEffect(() => {
    if (!onContextChange) return undefined;
    if (!response?.preview || !response.confirmationId) {
      onContextChange(null);
      return undefined;
    }

    onContextChange({
      title: response.preview.title,
      summary: response.preview.summary,
      selectedLabels: candidates
        .filter((candidate) => selectedIds.includes(candidate.id))
        .map((candidate) => candidate.fullName),
      editableLabels: editableCandidates.map((candidate) => candidate.fullName),
      editablePreview: editableCandidates
        .map((candidate) => editableContentById[candidate.id] ?? candidate.contentPreview ?? '')
        .find((content) => content.trim().length > 0) ?? null,
      applyChatEdit,
    });
    return () => onContextChange(null);
  }, [
    applyChatEdit,
    candidates,
    editableCandidates,
    editableContentById,
    onContextChange,
    response?.confirmationId,
    response?.preview,
    selectedIds,
  ]);

  if (!response?.preview || !response.confirmationId) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-domino-aubergine/35 p-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="tool-action-confirmation-title"
        className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-domino-cardBorder bg-domino-white shadow-[0_24px_80px_rgba(59,23,63,0.22)]"
      >
        <div className="flex items-start justify-between gap-4 border-b border-domino-cardBorder px-5 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-domino-lilac text-domino-aubergine">
              {isCrmPreview
                ? <Database className="h-4 w-4" />
                : <ListChecks className="h-4 w-4" />}
            </span>
            <div className="min-w-0">
              <h2 id="tool-action-confirmation-title" className="text-base font-semibold text-domino-ink">
                {response.preview.title}
              </h2>
              <p className="mt-1 text-sm text-domino-inkSoft">{response.preview.summary}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-domino-inkSoft hover:bg-domino-muted hover:text-domino-ink"
            aria-label="Fermer la confirmation"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4">
          {response.preview.companyStatus && (
            <p className="mb-4 rounded-lg border border-domino-cardBorder bg-domino-warm px-3 py-2 text-xs leading-5 text-domino-inkSoft">
              {response.preview.companyStatus}
            </p>
          )}
          <div className="divide-y divide-domino-cardBorder border-y border-domino-cardBorder">
            {candidates.map((candidate) => {
              const selectable = selectableIds.has(candidate.id);
              const selected = selectedIds.includes(candidate.id);
              const metadata = candidate.entityType === 'company'
                ? 'Entreprise'
                : candidate.entityType === 'note'
                ? `Note liée à ${candidate.company ?? 'l’entreprise'}`
                : candidate.entityType === 'task'
                ? 'Tâche'
                : candidate.entityType === 'artifact'
                ? 'Document'
                : candidate.entityType === 'action'
                ? 'Action'
                : [candidate.jobTitle, candidate.company, candidate.email].filter(Boolean).join(' · ') || 'Informations limitées';
              return (
                <div
                  key={candidate.id}
                  className={cn(
                    'flex items-start gap-3 py-3',
                    !selectable && 'opacity-75'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    disabled={!selectable || candidate.required || executing}
                    onChange={(event) => {
                      setSelectedIds((current) => event.target.checked
                        ? [...current, candidate.id]
                        : current.filter((id) => id !== candidate.id));
                    }}
                    className="mt-1 h-4 w-4 rounded border-domino-cardBorder text-domino-coral focus:ring-domino-coral"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-domino-ink">{candidate.fullName}</p>
                      <span className={cn('text-[11px] font-medium', STATE_STYLES[candidate.state])}>
                        {candidate.stateLabel}
                      </span>
                      {candidate.required && (
                        <span className="text-[11px] font-medium text-domino-inkSoft">Requis</span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-domino-inkSoft">
                      {metadata}
                    </p>
                    {candidate.detail && (
                      <p className="mt-1 flex items-start gap-1.5 text-xs leading-5 text-domino-inkSoft">
                        {candidate.state === 'ambiguous' || candidate.state === 'blocked'
                          ? <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                          : <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />}
                        {candidate.detail}
                      </p>
                    )}
                    {candidate.contentPreview && !candidate.editableFields?.includes('contentPreview') && (
                      <div className="mt-2 max-h-36 overflow-y-auto whitespace-pre-wrap rounded border border-domino-cardBorder bg-domino-warm px-3 py-2 text-xs leading-5 text-domino-inkSoft">
                        {candidate.contentPreview}
                      </div>
                    )}
                    {candidate.editableFields?.includes('contentPreview') && (
                      <Textarea
                        value={editableContentById[candidate.id] ?? candidate.contentPreview ?? ''}
                        rows={7}
                        disabled={executing || !selected}
                        className="mt-2 min-h-36 resize-y rounded border-domino-cardBorder bg-domino-warm text-xs leading-5 text-domino-ink"
                        onChange={(event) => {
                          setEditableContentById((current) => ({
                            ...current,
                            [candidate.id]: event.target.value,
                          }));
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-domino-cardBorder px-5 py-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={executing}>
            Annuler
          </Button>
          <Button
            type="button"
            onClick={() => onConfirm(selectedIds, confirmationEdits)}
            disabled={selectedCount === 0 || executing || hasInvalidSelectedEdit}
            loading={executing}
          >
            Confirmer {selectedCount > 0 ? `(${selectedCount})` : ''}
          </Button>
        </div>
      </div>
    </div>
  );
};
